import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import useIntersectionObserver from '../../hooks/useIntersect';
import {
  useGetThreadQuery,
  useDownvoteThreadMutation,
  useNeutralizeThreadMutation,
  useUpvoteThreadMutation,
} from '../../store/api/threadsApi';
import {
  asyncAddBookmark,
  asyncDeleteBookmark,
} from '../../store/plain/bookmarks/action';
import { ContentBody } from '../pieces/ContentBody';
import { ThreadFooter } from '../pieces/ThreadFooter';
import { AddCommentDialog } from './comments/AddCommentDialog';
import { FloatingReplyButton } from './comments/FloatingReplyButton';
import { Header } from './threadDetail/Header';

export function ThreadDetail() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { threadId } = useParams();
  const {
    isLoading,
    data: threadDetail,
    isError,
  } = useGetThreadQuery(threadId);
  const { bookmarks = [] } = useSelector((state) => state);
  const { status, currentUser } = useSelector((state) => state.auth);
  const authenticated = status === 'authenticated';
  const ref = useRef(null);
  const entry = useIntersectionObserver(ref, {});
  const isVisible = !!entry?.isIntersecting;

  const [isUpvoted, setIsUpvoted] = useState(false);
  const [isDownvoted, setIsDownvoted] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);

  const [upvoteThread] = useUpvoteThreadMutation();
  const [downvoteThread] = useDownvoteThreadMutation();
  const [neutralizeVote] = useNeutralizeThreadMutation();

  const baseUrl = window.location.origin;
  const sharedData = {
    url: `${baseUrl}/t/${location.state.thread.id}`,
    text: `${
      location.state.thread.body.length >= 240
        ? location.state.thread.body.substring(0, 239).concat('...')
        : location.state.thread.body
    }`,
    title: location.state.thread.title,
  };

  const onUpvoteButtonClick = () => {
    if (authenticated && isUpvoted) {
      const newUpvotesBy = threadDetail.upVotesBy.filter(
        (voterId) => voterId !== currentUser.id
      );
      neutralizeVote({
        threadId: threadDetail.id,
        newUpvotesBy,
        newDownvotesBy: threadDetail.downVotesBy,
      });
    } else if (authenticated && !isDownvoted) {
      const newUpvotesBy = [...threadDetail.upVotesBy, currentUser.id];
      upvoteThread({ threadId: threadDetail.id, newUpvotesBy });
    } else if (authenticated && isDownvoted) {
      const newDownvotesBy = threadDetail.downVotesBy.filter(
        (voterId) => voterId !== currentUser.id
      );
      const newUpvotesBy = [...threadDetail.upVotesBy, currentUser.id];
      neutralizeVote({
        threadId: threadDetail.id,
        newUpvotesBy,
        newDownvotesBy,
      });
      upvoteThread({ threadId: threadDetail.id, newUpvotesBy });
    } else {
      document.getElementById('loginDialog').showModal();
    }
  };

  const onDownvoteButtonClick = () => {
    if (authenticated && isDownvoted) {
      const newDownvotesBy = threadDetail.downVotesBy.filter(
        (voterId) => voterId !== currentUser.id
      );
      neutralizeVote({
        threadId: threadDetail.id,
        newUpvotesBy: threadDetail.upVotesBy,
        newDownvotesBy,
      });
    } else if (authenticated && !isUpvoted) {
      const newDownvotesBy = [...threadDetail.downVotesBy, currentUser.id];
      downvoteThread({ threadId: threadDetail.id, newDownvotesBy });
    } else if (authenticated && isUpvoted) {
      const newUpvotesBy = threadDetail.upVotesBy.filter(
        (voterId) => voterId !== currentUser.id
      );
      const newDownvotesBy = [...threadDetail.downVotesBy, currentUser.id];
      neutralizeVote({
        threadId: threadDetail.id,
        newUpvotesBy,
        newDownvotesBy,
      });
      downvoteThread({
        threadId: threadDetail.id,
        newUpvotesBy,
        newDownvotesBy,
      });
    } else {
      document.getElementById('loginDialog').showModal();
    }
  };

  const onCommentButtonClick = () => {
    if (authenticated && location.pathname.includes('/t/')) {
      document.getElementById('commentInput').focus();
    } else if (!authenticated) {
      document.getElementById('loginDialog').showModal();
    }
  };

  const onBookmarkButtonClick = async () => {
    const result = bookmarks.find(({ id }) => id === location.state.thread.id);
    if (result) {
      dispatch(asyncDeleteBookmark(location.state.thread.id));
    } else {
      dispatch(asyncAddBookmark(location.state.thread));
    }
  };

  const onShareButtonClick = () => {
    if (isSecureContext) {
      navigator.share(sharedData);
    } else {
      alert(
        'You cant do this in unsecured access. Share it from browser address bar instead.'
      );
    }
  };

  useEffect(() => {
    if (
      currentUser &&
      threadDetail &&
      threadDetail.upVotesBy.includes(currentUser.id)
    ) {
      setIsUpvoted(true);
    }
    if (
      currentUser &&
      threadDetail &&
      !threadDetail.upVotesBy.includes(currentUser.id)
    ) {
      setIsUpvoted(false);
    }
    if (
      currentUser &&
      threadDetail &&
      threadDetail.downVotesBy.includes(currentUser.id)
    ) {
      setIsDownvoted(true);
    }
    if (
      currentUser &&
      threadDetail &&
      !threadDetail.downVotesBy.includes(currentUser.id)
    ) {
      setIsDownvoted(false);
    }
  }, [currentUser, threadDetail]);

  useEffect(() => {
    if (bookmarks) {
      const result = bookmarks.find(
        ({ id }) => id === location.state.thread.id
      );
      if (result) setIsBookmarked(true);
    }
  }, [bookmarks, location.state.thread]);

  return (
    <>
      <article ref={ref} className="group">
        {isLoading || isError ? (
          <>
            <Header
              title={location.state.thread.title}
              userId={location.state.thread.ownerId}
              category={location.state.thread.category}
              createdAt={location.state.thread.createdAt}
              handleBookmarkButton={onBookmarkButtonClick}
              isBookmarked={isBookmarked}
            />
            <ContentBody text={location.state.thread.body} />
            <ThreadFooter
              upvotes={location.state.thread.upVotesBy}
              downvotes={location.state.thread.downVotesBy}
              isUpvoted={isUpvoted}
              isDownvoted={isDownvoted}
              commentsCount={location.state.thread.totalComments}
              handleCommentAction={onCommentButtonClick}
              handleUpvoteAction={onUpvoteButtonClick}
              handleDownvoteAction={onDownvoteButtonClick}
              handleShareAction={onShareButtonClick}
            />
          </>
        ) : (
          <>
            <Header
              title={threadDetail.title}
              userId={threadDetail.owner.id}
              category={threadDetail.category}
              createdAt={threadDetail.createdAt}
              handleBookmarkButton={onBookmarkButtonClick}
            />
            <ContentBody text={threadDetail.body} />
            <ThreadFooter
              upvotes={threadDetail.upVotesBy}
              downvotes={threadDetail.downVotesBy}
              isUpvoted={isUpvoted}
              isDownvoted={isDownvoted}
              commentsCount={threadDetail.comments.length}
              handleCommentAction={onCommentButtonClick}
              handleUpvoteAction={onUpvoteButtonClick}
              handleDownvoteAction={onDownvoteButtonClick}
              handleShareAction={onShareButtonClick}
            />
          </>
        )}
      </article>
      {authenticated && !isVisible ? (
        <>
          <FloatingReplyButton />
          <AddCommentDialog />
        </>
      ) : (
        ''
      )}
    </>
  );
}
